import type { EvergreenTimelineEvent, EvergreenTimelineEventType } from './types';

export const TIMELINE_DISPLAY_EVENT_TYPES = [
  'SURVEY',
  'QUIZ',
  'OFFER',
  'SHARED_RESOURCE',
  'PURCHASE_NOTIFICATION',
] as const satisfies readonly EvergreenTimelineEventType[];

export type TimelineDisplayEventType = (typeof TIMELINE_DISPLAY_EVENT_TYPES)[number];

// Ces events n'ont pas de HIDE : sans ça ils s'étaleraient jusqu'à la fin de la vidéo.
const INSTANT_EVENT_TYPES: readonly TimelineDisplayEventType[] = ['PURCHASE_NOTIFICATION'];

export type TimelineSegment = {
  id: string;
  eventType: TimelineDisplayEventType;
  resourceId: number | null;
  startSeconds: number;
  endSeconds: number;
  instant: boolean;
  event: EvergreenTimelineEvent;
};

/**
 * Une ressource partagée n'arrive jamais en eventType `SHARED_RESOURCE` : le back l'émet en
 * RESOURCE_SHOW / RESOURCE_HIDE et ne la distingue d'une slide ou d'une vidéo que par son
 * resourceType. Les autres interactions portent bien leur type métier.
 */
export function resolveDisplayEventType(
  event: EvergreenTimelineEvent,
): TimelineDisplayEventType | null {
  if (event.eventType === 'RESOURCE_SHOW' || event.eventType === 'RESOURCE_HIDE') {
    return event.resourceType === 'SHARED_RESOURCE' ? 'SHARED_RESOURCE' : null;
  }
  return (TIMELINE_DISPLAY_EVENT_TYPES as readonly string[]).includes(event.eventType)
    ? (event.eventType as TimelineDisplayEventType)
    : null;
}

export function isDisplayableTimelineEvent(event: EvergreenTimelineEvent): boolean {
  return resolveDisplayEventType(event) !== null;
}

export function isHideEvent(event: EvergreenTimelineEvent): boolean {
  if (event.eventType === 'RESOURCE_HIDE') return true;
  try {
    const payload = event.payloadJson ? JSON.parse(event.payloadJson) : null;
    return payload?.action === 'HIDE';
  } catch {
    return false;
  }
}

/**
 * Une notification d'achat rejouée ne référence pas toujours son offre d'origine : le message
 * diffusé au spectateur est alors le seul libellé disponible.
 */
export function getPurchaseNotificationMessage(payloadJson: string | null): string | null {
  if (!payloadJson) return null;
  try {
    const payload = JSON.parse(payloadJson);
    const message = payload?.public?.message;
    return typeof message === 'string' && message.trim().length > 0 ? message : null;
  } catch {
    return null;
  }
}

export function sortTimelineEvents(events: EvergreenTimelineEvent[]): EvergreenTimelineEvent[] {
  return [...events].sort(
    (a, b) => a.startOffsetSeconds - b.startOffsetSeconds || a.displayOrder - b.displayOrder,
  );
}

export type TimelineListEntry = {
  key: string;
  event: EvergreenTimelineEvent;
  offsetSeconds: number;
  isHide: boolean;
};

/**
 * Lignes de la liste chronologique : un créneau programmé porte sa fin dans l'évènement,
 * il est donc dédoublé en une apparition et un retrait, comme une paire SHOW/HIDE héritée d'un Live.
 */
export function buildTimelineListEntries(events: EvergreenTimelineEvent[]): TimelineListEntry[] {
  const entries: TimelineListEntry[] = [];
  for (const event of events) {
    entries.push({
      key: `${event.id}`,
      event,
      offsetSeconds: event.startOffsetSeconds,
      isHide: isHideEvent(event),
    });
    if (event.endOffsetSeconds != null) {
      entries.push({
        key: `${event.id}-end`,
        event,
        offsetSeconds: event.endOffsetSeconds,
        isHide: true,
      });
    }
  }
  return entries.sort(
    (a, b) =>
      a.offsetSeconds - b.offsetSeconds ||
      Number(b.isHide) - Number(a.isHide) ||
      a.event.displayOrder - b.event.displayOrder,
  );
}

export function buildTimelineSegments(
  events: EvergreenTimelineEvent[],
  videoDuration: number,
): TimelineSegment[] {
  const segments: TimelineSegment[] = [];
  const sorted = sortTimelineEvents(events);
  const showEvents = sorted.filter((e) => !isHideEvent(e));
  const availableHideEvents = sorted.filter(isHideEvent);

  for (const show of showEvents) {
    if (show.startOffsetSeconds >= videoDuration) continue;
    const displayEventType = resolveDisplayEventType(show);
    if (displayEventType === null) continue;

    if (INSTANT_EVENT_TYPES.includes(displayEventType)) {
      segments.push({
        id: `segment-${show.id}`,
        eventType: displayEventType,
        resourceId: show.resourceId,
        startSeconds: show.startOffsetSeconds,
        endSeconds: show.startOffsetSeconds,
        instant: true,
        event: show,
      });
      continue;
    }

    let hideOffset: number | null = show.endOffsetSeconds;
    // Un créneau programmé porte sa fin ; seuls les évènements hérités d'un Live ont un HIDE séparé.
    if (hideOffset == null) {
      const hideIndex = availableHideEvents.findIndex(
        (h) =>
          resolveDisplayEventType(h) === displayEventType &&
          h.resourceId === show.resourceId &&
          h.startOffsetSeconds > show.startOffsetSeconds,
      );
      // Une même ressource peut être affichée plusieurs fois : un HIDE ne sert qu'une fois.
      const hide = hideIndex >= 0 ? availableHideEvents.splice(hideIndex, 1)[0] : undefined;
      hideOffset = hide ? hide.startOffsetSeconds : null;
    }
    const end = Math.min(hideOffset ?? videoDuration, videoDuration);
    if (end <= show.startOffsetSeconds) continue;

    segments.push({
      id: `segment-${show.id}`,
      eventType: displayEventType,
      resourceId: show.resourceId,
      startSeconds: show.startOffsetSeconds,
      endSeconds: end,
      instant: false,
      event: show,
    });
  }

  return segments;
}

export function getEvergreenTimelineResourceIds(
  timeline: EvergreenTimelineEvent[] | undefined | null,
  eventType: TimelineDisplayEventType,
): Set<number> {
  const ids = new Set<number>();
  if (!timeline?.length) return ids;
  for (const e of timeline) {
    if (resolveDisplayEventType(e) === eventType && typeof e.resourceId === 'number') {
      ids.add(e.resourceId);
    }
  }
  return ids;
}
